import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { X, Sparkles, ArrowLeft, Clock } from 'lucide-react';

export const SmartCampaignModal = ({ campaign, onDismiss, onTrackClick, onTrackView }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  // Trigger & Timing Rules (Immediate, Delay, Scroll, Exit Intent)
  useEffect(() => {
    if (!campaign) {
      setIsOpen(false);
      return;
    }

    const trigger = campaign.triggerType || 'DELAY';
    const delaySec = campaign.delaySeconds !== undefined ? campaign.delaySeconds : 6;
    const scrollPct = campaign.scrollPercentage !== undefined ? campaign.scrollPercentage : 25;

    let delayTimer = null;
    let autoCloseTimer = null;

    const showModal = () => {
      setIsOpen(true);
      onTrackView?.(campaign);

      // Auto-hide after N seconds
      if (campaign.autoCloseSeconds && campaign.autoCloseSeconds > 0) {
        autoCloseTimer = setTimeout(() => {
          setIsOpen(false);
          onDismiss?.(campaign);
        }, campaign.autoCloseSeconds * 1000);
      }
    };

    if (trigger === 'IMMEDIATE') {
      showModal();
    } else if (trigger === 'DELAY') {
      delayTimer = setTimeout(showModal, Math.max(delaySec, 1) * 1000);
    } else if (trigger === 'SCROLL') {
      const handleScroll = () => {
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollHeight > 0) {
          const percent = (window.scrollY / scrollHeight) * 100;
          if (percent >= scrollPct) {
            showModal();
            window.removeEventListener('scroll', handleScroll);
          }
        }
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', handleScroll);
        if (delayTimer) clearTimeout(delayTimer);
        if (autoCloseTimer) clearTimeout(autoCloseTimer);
      };
    } else if (trigger === 'EXIT_INTENT') {
      const handleMouseLeave = (e) => {
        if (e.clientY <= 15) {
          showModal();
          document.removeEventListener('mouseleave', handleMouseLeave);
        }
      };
      document.addEventListener('mouseleave', handleMouseLeave);
      return () => {
        document.removeEventListener('mouseleave', handleMouseLeave);
        if (delayTimer) clearTimeout(delayTimer);
        if (autoCloseTimer) clearTimeout(autoCloseTimer);
      };
    }

    return () => {
      if (delayTimer) clearTimeout(delayTimer);
      if (autoCloseTimer) clearTimeout(autoCloseTimer);
    };
  }, [campaign?.id, campaign?.triggerType, campaign?.delaySeconds, campaign?.scrollPercentage, campaign?.autoCloseSeconds]);

  // Live countdown calculation
  useEffect(() => {
    if (!campaign?.hasCountdown || !campaign?.endDate) {
      setTimeLeft(null);
      return;
    }

    const calculateTime = () => {
      const difference = new Date(campaign.endDate).getTime() - Date.now();
      if (difference <= 0) {
        setTimeLeft(null);
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((difference / 1000 / 60) % 60);

      setTimeLeft({
        days: String(days).padStart(2, '0'),
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
      });
    };

    calculateTime();
    const interval = setInterval(calculateTime, 60000);
    return () => clearInterval(interval);
  }, [campaign?.endDate, campaign?.hasCountdown]);

  if (!campaign || !isOpen) return null;

  const handleClose = () => {
    setIsOpen(false);
    onDismiss?.(campaign);
  };

  const handleAction = () => {
    onTrackClick?.(campaign);
    setIsOpen(false);
  };

  const customBg = campaign.bgColor || '#0A235C';
  const customText = campaign.textColor || '#FFFFFF';
  const customBtnBg = campaign.buttonColor || '#13C5E9';
  const customBtnText = campaign.buttonTextColor || '#0A235C';

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4 bg-black/60 backdrop-blur-sm" dir="rtl">
        
        {/* Desktop Modal & Mobile Bottom Sheet */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.96 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          style={{ backgroundColor: customBg, color: customText }}
          className="relative w-full max-w-lg border border-white/15 rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden"
        >
          {/* Close Button */}
          {campaign.dismissible && (
            <button
              onClick={handleClose}
              aria-label="إغلاق"
              className="absolute top-4 left-4 z-10 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {/* Optional Banner Image */}
          {campaign.imageUrl && (
            <div className="relative aspect-[21/9] w-full overflow-hidden bg-black/40">
              <img
                src={campaign.imageUrl}
                alt={campaign.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>
          )}

          <div className="p-6 md:p-8">
            {/* Badge & Countdown */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {campaign.badgeText && (
                <span className="inline-flex items-center gap-1 bg-white/20 text-white border border-white/30 font-bold px-3 py-1 rounded-full text-xs shadow-sm">
                  <Sparkles className="w-3.5 h-3.5" />
                  {campaign.badgeText}
                </span>
              )}
              {timeLeft && (
                <span className="inline-flex items-center gap-1.5 bg-black/30 border border-white/10 text-white/90 text-xs font-mono px-2.5 py-1 rounded-lg">
                  <Clock className="w-3 h-3 text-[#13C5E9]" />
                  <span>ينتهي خلال: </span>
                  <span className="text-[#FF8A1F] font-bold">{timeLeft.days}</span>ي :{' '}
                  <span className="text-[#FF8A1F] font-bold">{timeLeft.hours}</span>س :{' '}
                  <span className="text-[#FF8A1F] font-bold">{timeLeft.minutes}</span>د
                </span>
              )}
            </div>

            {/* Title */}
            <h3 className="text-xl md:text-2xl font-black mb-2.5 leading-snug">
              {campaign.title}
            </h3>

            {/* Subtitle / Description */}
            {campaign.subtitle && (
              <p className="opacity-85 text-xs md:text-sm leading-relaxed mb-6">
                {campaign.subtitle}
              </p>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2 border-t border-white/10">
              {campaign.ctaText && campaign.ctaUrl && (
                <Link
                  to={campaign.ctaUrl}
                  onClick={handleAction}
                  style={{ backgroundColor: customBtnBg, color: customBtnText }}
                  className="flex-1 py-3 px-5 font-black text-sm rounded-xl shadow-lg text-center transition-all flex items-center justify-center gap-2 group hover:opacity-90"
                >
                  <span>{campaign.ctaText}</span>
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                </Link>
              )}

              {campaign.dismissible && (
                <button
                  onClick={handleClose}
                  className="py-3 px-4 rounded-xl text-white/70 hover:text-white hover:bg-white/10 font-semibold text-xs transition-colors"
                >
                  لاحقاً
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
